import { useCallback, useEffect, useRef, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query/react';
import {
	useGetMarketsQuery,
	useLazyGetTradesQuery,
	useLazyGetLobbyQuery,
	useLazyGetEventQuery,
} from '@/store/api';
import { ROUTES } from '@/constants';
import { asApiError } from '@/utils';
import { UserRole, LobbyTypeEnum } from '@sportspredict/shared';

interface UseLobbyEventDataProps {
	lobbyId: string | undefined;
	eventId: string | undefined;
	user: any;
	isAdmin: boolean;
	navigate: (path: string) => void;
}

export function useLobbyEventData({
	lobbyId,
	eventId,
	user,
	isAdmin,
	navigate,
}: UseLobbyEventDataProps) {
	const [data, setData] = useState<{ eventResponse: any; lobbyResponse: any }>({
		eventResponse: null,
		lobbyResponse: null,
	});
	const { eventResponse, lobbyResponse } = data;

	const [markets, setMarkets] = useState<any[]>([]);
	const [userTrades, setUserTrades] = useState<any[]>([]);
	const [error, setError] = useState<string | null>(null);

	const [fetchEvent] = useLazyGetEventQuery();
	const [fetchTrades] = useLazyGetTradesQuery();
	const [fetchLobby] = useLazyGetLobbyQuery();

	const { currentData: fetchedMarkets, refetch: refetchMarkets } = useGetMarketsQuery(
		eventId && lobbyId ? { event_id: eventId, lobby_id: lobbyId } : skipToken
	);

	// The only place that resets markets: a refetch returning unchanged data keeps the same
	// reference, so clearing them anywhere else would leave the lobby empty until the data changes.
	useEffect(() => {
		setMarkets(fetchedMarkets ?? []);
	}, [fetchedMarkets]);

	const fetchAllRef = useRef<() => Promise<void>>(undefined);
	const [userTradesLoaded, setUserTradesLoaded] = useState(false);

	const mergeLobbyResponse = useCallback((nextLobby: any) => {
		if (!nextLobby) return;

		setData((prev) => ({
			...prev,
			lobbyResponse: prev.lobbyResponse ? { ...prev.lobbyResponse, ...nextLobby } : nextLobby,
		}));
	}, []);

	useEffect(() => {
		if (!lobbyId || !eventId) return;

		let isCurrentLobby = true;

		setUserTrades([]);
		setUserTradesLoaded(false);
		setError(null);

		const fetchAll = async () => {
			let lobby: any;
			let event: any;

			try {
				[event, lobby] = await Promise.all([
					fetchEvent(eventId).unwrap(),
					fetchLobby(lobbyId).unwrap(),
				]);

				if (!isCurrentLobby) return;

				if (!event || !lobby) {
					console.warn('Event or Lobby not found. Redirecting to Home.');
					navigate(ROUTES.HOME);
					return;
				}

				setData((prev) => ({
					...prev,
					eventResponse: event,
					lobbyResponse: lobby,
				}));
			} catch (error) {
				if (!isCurrentLobby) return;

				console.error('Error loading Event or Lobby', error);
				navigate(ROUTES.HOME);
				return;
			}

			try {
				const shouldLoadUserTrades =
					user?.role === UserRole.User && lobby?.type !== LobbyTypeEnum.CrowdLock;
				const [userTradeData] = await Promise.all([
					shouldLoadUserTrades
						? fetchTrades({ user_id: user.id, event_id: eventId, lobby_id: lobbyId }).unwrap()
						: Promise.resolve([]),
					refetchMarkets(),
				]);

				if (!isCurrentLobby) return;

				const currentLobbyTrades = (userTradeData || []).filter(
					(trade: any) => String(trade.lobby_id) === String(lobbyId)
				);

				setUserTrades(currentLobbyTrades);
			} catch (err: unknown) {
				if (!isCurrentLobby) return;

				setError(asApiError(err).message || 'Unknown error');
				console.error('Failed to load data', err);
			} finally {
				if (isCurrentLobby) {
					setUserTradesLoaded(true);
				}
			}
		};

		fetchAllRef.current = fetchAll;
		fetchAll();

		return () => {
			isCurrentLobby = false;
		};
	}, [lobbyId, eventId, user, isAdmin, navigate]);

	const currentEventListener = useCallback(
		(socketData: any) => {
			if (socketData?.event?.id === eventId) {
				setData((prev) => ({ ...prev, eventResponse: socketData.event }));
			}

			if (String(socketData?.lobby?.id) !== String(lobbyId)) return;
			mergeLobbyResponse(socketData.lobby);
		},
		[lobbyId, eventId, mergeLobbyResponse]
	);

	const didRefetchAfterRevivalRef = useRef(false);
	useEffect(() => {
		if (!data?.lobbyResponse?.is_revival_initiated || !lobbyId) {
			didRefetchAfterRevivalRef.current = false;
			return;
		}

		if (didRefetchAfterRevivalRef.current) return;
		didRefetchAfterRevivalRef.current = true;

		const refetchLobby = async () => {
			try {
				const lobby = await fetchLobby(lobbyId).unwrap();
				if (lobby) {
					setData((prev) => ({ ...prev, lobbyResponse: lobby }));
				}
			} catch (e) {
				console.error('[refetch lobby after revival]', e);
			}
		};

		refetchLobby();
	}, [data?.lobbyResponse?.is_revival_initiated, lobbyId]);

	return {
		data,
		setData,
		eventResponse,
		lobbyResponse,
		markets,
		setMarkets,
		userTrades,
		setUserTrades,
		error,
		userTradesLoaded,
		mergeLobbyResponse,
		fetchAllRef,
		currentEventListener,
	};
}
